import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image, StyleSheet, View } from 'react-native';
import MapGL, {
  AttributionControl,
  type MapRef,
  Marker as MapMarker,
  type MarkerEvent,
  Popup,
  type ViewState,
  type ViewStateChangeEvent,
} from '@vis.gl/react-maplibre';
import { LoadingDots } from '@sg/garnish';
import 'maplibre-gl/dist/maplibre-gl.css';

import { Callout } from '../Callout';
import { useActivePin, useCalloutPress, useNotMinimizedPins } from './hooks';
import {
  DEFAULT_EDGE_PADDINGS,
  MARKER_SIZE,
  MAX_ZOOM_LEVEL,
  PIN_IMAGES,
  TRANSITION_DURATION_WEB,
} from './Map.constants';
import type { PinData, Props } from './Map.types';
import {
  createRadarStyleURL,
  getPinImageKey,
  getPinsBounds,
  initializeRadarIfNeeded,
  overridePopupStyles,
} from './utils';

// ─────────────────────────────────────────────────────────────────────────────

export const Map = (props: Props) => {
  const {
    isAutoFitBoundsEnabled,
    edgePadding,
    publishableKey,
    region,
    pins,
    activePinId,
    customerPositionPin,
    mapRefWeb,
    onRegionChange,
    onMapPress,
    onPinPress,
    onCalloutPress,
    onZoomChange,
  } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const localMapRef = useRef<MapRef>(null);
  const mapRef = mapRefWeb || localMapRef;

  // ─── State ───────────────────────────────────────────────────────────

  const [styleURL, setStyleURL] = useState<string | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // ─── Derived Data ────────────────────────────────────────────────────

  const isReady = Boolean(styleURL);
  const filteredPins = useNotMinimizedPins(pins);

  const mapViewportPadding = useMemo(() => {
    const { 0: top, 1: right, 2: bottom, 3: left } = DEFAULT_EDGE_PADDINGS;

    return {
      top: edgePadding?.top ?? top,
      right: edgePadding?.right ?? right,
      bottom: edgePadding?.bottom ?? bottom,
      left: edgePadding?.left ?? left,
    };
  }, [edgePadding]);

  const activePin = useActivePin({
    activePinId,
    customerPositionPin,
    filteredPins,
  });

  const pinsBounds = useMemo(() => getPinsBounds(filteredPins), [filteredPins]);

  const initialViewState = useMemo<Partial<ViewState>>(() => {
    if (region.topLeft && region.bottomRight) {
      return {
        bounds: {
          north: region.topLeft.latitude,
          south: region.bottomRight.latitude,
          east: region.bottomRight.longitude,
          west: region.topLeft.longitude,
        },
        padding: mapViewportPadding,
      };
    }

    return {
      longitude: region.lng,
      latitude: region.lat,
      zoom: region.zoom ?? 13,
    };
  }, [region, mapViewportPadding]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const initializeRadar = useCallback(() => {
    initializeRadarIfNeeded(publishableKey).then(() => {
      createRadarStyleURL(publishableKey).then(setStyleURL);
    });
  }, [publishableKey]);

  const handleViewStateChange = useCallback(
    (event: ViewStateChangeEvent) => {
      const map = mapRef.current?.getMap();

      if (!map) return;

      const { viewState } = event;
      const { longitude: lng, latitude: lat, zoom } = viewState;

      const bounds = map.getBounds();

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const topLeft = { latitude: ne.lat, longitude: sw.lng };
      const bottomRight = { latitude: sw.lat, longitude: ne.lng };

      onRegionChange?.({ lat, lng, zoom, topLeft, bottomRight });
      onZoomChange?.(zoom);
    },
    [onRegionChange, onZoomChange, mapRef],
  );

  const handlePinPress = useCallback(
    (pin: PinData) => {
      onPinPress?.(pin.id);

      mapRef.current?.flyTo({
        center: [pin.lng, pin.lat],
        duration: TRANSITION_DURATION_WEB,
      });
    },
    [onPinPress, mapRef],
  );

  const handleCalloutPress = useCalloutPress({
    activePin,
    onCalloutPress,
  });

  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true);
  }, []);

  const fitBounds = useCallback(() => {
    if (
      !isReady ||
      !isMapLoaded ||
      !isAutoFitBoundsEnabled ||
      !mapRef.current ||
      !pinsBounds
    )
      return;

    const map = mapRef.current?.getMap();

    if (!map) return;

    const [neLng, neLat] = pinsBounds.ne;
    const [swLng, swLat] = pinsBounds.sw;

    map.fitBounds(
      [[swLng, swLat], [neLng, neLat]], // prettier-ignore
      { padding: mapViewportPadding, duration: TRANSITION_DURATION_WEB },
    );
  }, [
    isAutoFitBoundsEnabled,
    isReady,
    isMapLoaded,
    pinsBounds,
    mapViewportPadding,
    mapRef,
  ]);

  const updateRegion = useCallback(() => {
    if (pinsBounds) return;

    if (!region) return;

    if (region.topLeft && region.bottomRight) {
      mapRef.current?.fitBounds(
        [
          [region.topLeft.longitude, region.topLeft.latitude],
          [region.bottomRight.longitude, region.bottomRight.latitude],
        ],
        { padding: mapViewportPadding, duration: TRANSITION_DURATION_WEB },
      );

      return;
    }

    mapRef.current?.flyTo({
      center: [region.lng, region.lat],
      duration: TRANSITION_DURATION_WEB,
      padding: mapViewportPadding,
      zoom: region.zoom ?? 13,
    });
  }, [region, mapRef, pinsBounds, mapViewportPadding]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(overridePopupStyles, []);
  useEffect(initializeRadar, [initializeRadar]);
  useEffect(updateRegion, [updateRegion]);
  useEffect(fitBounds, [fitBounds]);

  // ─────────────────────────────────────────────────────────────────────

  if (!styleURL) {
    return <LoadingDots />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <MapGL
      ref={mapRef}
      mapStyle={styleURL}
      initialViewState={initialViewState}
      padding={mapViewportPadding}
      onMove={handleViewStateChange}
      onMoveEnd={handleViewStateChange}
      onClick={onMapPress}
      onLoad={handleMapLoad}
      maxZoom={MAX_ZOOM_LEVEL}
      style={MAP_INLINE_STYLES}
      fadeDuration={1000}
      attributionControl={false}
      maplibreLogo={false}
    >
      <AttributionControl compact={true} position="bottom-left" />

      {filteredPins.map((pin) => (
        <Marker key={pin.id} pin={pin} onPress={handlePinPress} />
      ))}

      {customerPositionPin ? (
        <Marker pin={customerPositionPin} onPress={handlePinPress} />
      ) : null}

      {activePin ? (
        <Popup
          longitude={activePin.lng}
          latitude={activePin.lat}
          anchor="bottom"
          closeButton={false}
          offset={[0, -MARKER_SIZE]}
          closeOnClick={false}
        >
          <Callout title={activePin.name} onPress={handleCalloutPress} />
        </Popup>
      ) : null}
    </MapGL>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const Marker = (props: MarkerProps) => {
  const { pin, onPress } = props;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleClick = useCallback(
    (event: MarkerEvent<MouseEvent>) => {
      event.originalEvent.stopPropagation();

      onPress(pin);
    },
    [pin, onPress],
  );

  // ─────────────────────────────────────────────────────────────────────

  const pinImage = PIN_IMAGES[getPinImageKey(pin)];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <MapMarker
      longitude={pin.lng}
      latitude={pin.lat}
      anchor="bottom"
      onClick={handleClick}
      style={MARKER_INLINE_STYLES}
    >
      <View style={styles.markerContainer}>
        <Image
          source={pinImage}
          style={styles.markerImage}
          resizeMode="contain"
          accessibilityLabel={pin.name}
        />
      </View>
    </MapMarker>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  markerContainer: {
    width: MARKER_SIZE,
    height: MARKER_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  markerImage: {
    width: MARKER_SIZE,
    height: MARKER_SIZE,
  },
});

const MAP_INLINE_STYLES = {
  width: '100%',
  height: '100%',
};

const MARKER_INLINE_STYLES = {
  cursor: 'pointer',
};

// ─── Types ───────────────────────────────────────────────────────────────────

type MarkerProps = {
  pin: PinData;
  onPress: (pin: PinData) => void;
};
